import React from "react"

import {
  CopyableId,
  DrawerBody,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@coval/ui"

import { DrawerOpenInNewTabAction } from "@/components/drawer/DrawerOpenInNewTabAction"
import { readOnlyInteractionGuardProps } from "@/components/version-history/readOnlyGuard"
import type { Persona } from "@/types/api/Persona"

import { usePersonaFormController } from "../hooks/usePersonaFormController"
import { usePersonaById } from "../routes/usePersonasTransport"
import { PersonaFormFields } from "./PersonaFormFields"
import {
  PersonaFormFieldsInitializationError,
  PersonaFormFieldsSkeleton,
} from "./PersonaFormFieldsSkeleton"

/**
 * The persona as the conversation that opened this drawer actually ran it. A
 * result captures the name and prompt it used; the persona record itself is
 * fetched live and may since have been edited or deleted, so the two are shown
 * together rather than the live one standing in for what ran.
 */
export interface RunPersonaSnapshot {
  displayName?: string | null
  prompt?: string | null
}

interface PersonaDrawerPanelProps {
  personaId: string
  runPersona?: RunPersonaSnapshot | null
}

function PersonaDrawerContent({ persona }: { persona: Persona }) {
  const controller = usePersonaFormController({
    selectedPersona: persona,
    isOpen: true,
  })

  if (controller.hasInitializationError) {
    return <PersonaFormFieldsInitializationError />
  }

  if (!controller.isFormInitialized) {
    return <PersonaFormFieldsSkeleton />
  }

  return (
    <fieldset
      disabled
      className="m-0 min-w-0 space-y-6 border-0 p-0"
      {...readOnlyInteractionGuardProps()}
    >
      <PersonaFormFields
        controller={controller}
        showCharacteristicsError={false}
      />
    </fieldset>
  )
}

/**
 * One persona's read-only detail view, as the body of a drawer the caller owns.
 * The caller supplies the drawer so a single panel can swap between resources
 * instead of stacking one drawer per kind.
 */
export const PersonaDrawerPanel: React.FC<PersonaDrawerPanelProps> = ({
  personaId,
  runPersona,
}) => {
  const { data: persona, isLoading } = usePersonaById(personaId)

  // Either captured field stands on its own: a result that kept only the name
  // still says what the persona was called when it ran, which is the whole
  // question once the live record has been renamed or deleted.
  const capturedName = runPersona?.displayName?.trim()
  const capturedPrompt = runPersona?.prompt?.trim()

  return (
    <>
      <DrawerHeader
        actions={
          <DrawerOpenInNewTabAction
            path={`/personas/${personaId}`}
            label="Open persona in new tab"
          />
        }
      >
        <DrawerTitle>
          {isLoading ? "Loading…" : (persona?.display_name ?? "Persona")}
        </DrawerTitle>
        {persona ? (
          <DrawerDescription className="flex items-center gap-1">
            <span>ID:</span>
            <CopyableId id={persona.id} label="Copy persona ID" />
          </DrawerDescription>
        ) : null}
      </DrawerHeader>
      <DrawerBody>
        {capturedName || capturedPrompt ? (
          <div className="mb-8 space-y-2">
            <h3 className="text-sm font-semibold text-coval-text">
              From this conversation
            </h3>
            <div className="rounded-lg border border-coval-border bg-surface-default p-4 space-y-3">
              {capturedName ? (
                <div className="space-y-1">
                  <p className="text-xs font-semibold uppercase tracking-normal text-coval-text-muted">
                    Name
                  </p>
                  <p className="text-sm text-coval-text">{capturedName}</p>
                </div>
              ) : null}
              {capturedPrompt ? (
                <div className="space-y-1">
                  <p className="text-xs font-semibold uppercase tracking-normal text-coval-text-muted">
                    Prompt
                  </p>
                  <p className="text-sm text-coval-text whitespace-pre-wrap break-words">
                    {capturedPrompt}
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
        {isLoading ? (
          <PersonaFormFieldsSkeleton />
        ) : persona ? (
          <PersonaDrawerContent persona={persona} />
        ) : (
          <p className="text-coval-text-muted text-sm">Persona not found.</p>
        )}
      </DrawerBody>
    </>
  )
}
